/**
 * <s-collection-directory> — Client-side filtering for the Search page collection directory.
 *
 * Every collection is server-rendered, so the directory works fully without JavaScript; this
 * element only narrows what is already on the page.
 *
 * Child markup:
 *   [data-directory-filter]   — Text input; matches collection titles as the shopper types.
 *   [data-directory-tab]      — Toggle buttons with `data-keywords` (comma-separated, may be empty
 *                               for "All"). A collection matches a tab when its title contains any keyword;
 *                               a keyword starting with "=" must match the whole title.
 *   [data-directory-item]     — One per collection link, with `data-title`.
 *   [data-directory-group]    — Letter or year groups; hidden when none of their items match.
 *                               `data-collapsible` groups collapse extra items until expanded.
 *   [data-directory-toggle]   — Show all / Show less buttons (`command="--toggle-group"`, `data-group`).
 *   [data-directory-jump]     — Letter-bar links; hidden when their target group has no matches.
 *   [data-directory-bar]      — Sticky bar; its height feeds the groups' scroll offset.
 *   [data-directory-status]   — Polite live region for the match count.
 *   [data-directory-empty]    — Shown when nothing matches.
 *
 * Sets `--directory-top` (sticky header height) and `--directory-bar` (bar height) on itself.
 */
const normalize = (value) =>
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

class CollectionDirectory extends HTMLElement {
  #ac;
  #ro;
  #query = '';
  #keywords = [];
  #expanded = new Set();
  #titles = new Map();

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.input = this.querySelector('[data-directory-filter]');
    this.tabs = [...this.querySelectorAll('[data-directory-tab]')];
    this.items = [...this.querySelectorAll('[data-directory-item]')];
    this.groups = [...this.querySelectorAll('[data-directory-group]')];
    this.toggles = [...this.querySelectorAll('[data-directory-toggle]')];
    this.jumps = [...this.querySelectorAll('[data-directory-jump]')];
    this.status = this.querySelector('[data-directory-status]');
    this.empty = this.querySelector('[data-directory-empty]');
    this.bar = this.querySelector('[data-directory-bar]');

    for (const item of this.items) this.#titles.set(item, normalize(item.dataset.title));

    this.input?.addEventListener(
      'input',
      () => {
        this.#query = normalize(this.input.value);
        this.#apply();
      },
      opts,
    );

    for (const tab of this.tabs) {
      tab.addEventListener(
        'click',
        () => {
          for (const other of this.tabs) other.setAttribute('aria-pressed', String(other === tab));
          this.#keywords = (tab.dataset.keywords || '').split(',').map(normalize).filter(Boolean);
          this.#apply();
        },
        opts,
      );
    }

    this.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--toggle-group') return;
        const id = event.source?.dataset.group;
        if (!id) return;
        if (this.#expanded.has(id)) this.#expanded.delete(id);
        else this.#expanded.add(id);
        this.#apply();
      },
      opts,
    );

    // Keep the sticky bar tucked under the sticky site header, and offset jump targets below both.
    const header = this.ownerDocument.querySelector('header');
    this.#ro = new ResizeObserver(() => {
      this.style.setProperty('--directory-top', `${header ? header.offsetHeight : 0}px`);
      this.style.setProperty('--directory-bar', `${this.bar ? this.bar.offsetHeight : 0}px`);
    });
    if (header) this.#ro.observe(header);
    if (this.bar) this.#ro.observe(this.bar);

    this.#apply();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ro?.disconnect();
  }

  #apply() {
    const filtering = Boolean(this.#query) || this.#keywords.length > 0;
    let visible = 0;

    for (const item of this.items) {
      const title = this.#titles.get(item) || '';
      const matchesQuery = !this.#query || title.includes(this.#query);
      const matchesTab =
        this.#keywords.length === 0 ||
        this.#keywords.some((k) => (k.startsWith('=') ? title === k.slice(1) : title.includes(k)));
      const show = matchesQuery && matchesTab;
      item.hidden = !show;
      if (show && item.hasAttribute('data-directory-count')) visible += 1;
    }

    for (const group of this.groups) {
      group.hidden = !group.querySelector('[data-directory-item]:not([hidden])');
      if (group.hasAttribute('data-collapsible')) {
        group.toggleAttribute('data-collapsed', !filtering && !this.#expanded.has(group.id));
      }
    }

    for (const toggle of this.toggles) {
      const expanded = this.#expanded.has(toggle.dataset.group);
      toggle.hidden = filtering;
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
    }

    for (const jump of this.jumps) {
      const target = this.querySelector(jump.getAttribute('href'));
      const holder = jump.closest('li') || jump;
      holder.hidden = !target || target.hidden;
    }

    if (this.status) {
      const template = visible === 1 ? this.status.dataset.one : this.status.dataset.other;
      this.status.textContent = filtering ? (template || '').replace('[count]', String(visible)) : '';
    }
    if (this.empty) this.empty.hidden = visible > 0;
  }
}

if (!customElements.get('s-collection-directory')) {
  customElements.define('s-collection-directory', CollectionDirectory);
}
